// Locus Knowledge Graph Samples
// Organized by difficulty levels with systematic naming

export const locusSamples = {
  fundamental: [
    {
      id: 'locus_fund_01',
      problem: "Find the locus of points equidistant from two fixed points A(0, 0) and B(4, 0)",
      problemType: "locus",
      difficulty: "fundamental",
      description: "Basic perpendicular bisector locus",
      graphData: {
        nodes: [
          {
            id: "point_a",
            name: "Point A",
            type: "concepts",
            description: "First fixed point",
            value: "A(0, 0)",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "point_b",
            name: "Point B",
            type: "concepts",
            description: "Second fixed point",
            value: "B(4, 0)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "equidistant_condition",
            name: "Equidistant Condition",
            type: "concepts",
            description: "Points equidistant from A and B",
            value: "PA = PB",
            importance: 10,
            x: 150, y: -100, z: 20
          },
          {
            id: "distance_formula",
            name: "Distance Formula",
            type: "formulas",
            description: "Distance between points: √[(x-x₁)² + (y-y₁)²]",
            value: "√(x² + y²) = √[(x-4)² + y²]",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "perpendicular_bisector_theorem",
            name: "Perpendicular Bisector Theorem",
            type: "formulas",
            description: "Locus of points equidistant from two fixed points is perpendicular bisector",
            value: "PA = PB → perpendicular bisector of AB",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "midpoint_formula",
            name: "Midpoint Formula",
            type: "formulas",
            description: "Midpoint of AB: ((x₁+x₂)/2, (y₁+y₂)/2)",
            value: "Midpoint = ((0+4)/2, (0+0)/2) = (2, 0)",
            importance: 9,
            x: 0, y: 100, z: 40
          },
          {
            id: "perpendicular_bisector_formula",
            name: "Perpendicular Bisector Formula",
            type: "formulas",
            description: "Perpendicular bisector passes through midpoint",
            value: "x = 2 (vertical line through midpoint)",
            importance: 10,
            x: 0, y: 150, z: 40
          },
          {
            id: "identify_points",
            name: "Identify Points",
            type: "steps",
            description: "Recognize the fixed points",
            value: "Step 1: A(0, 0), B(4, 0)",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "write_distance_equation",
            name: "Write Distance Equation",
            type: "steps",
            description: "Set up the distance equation",
            value: "Step 2: √(x² + y²) = √[(x-4)² + y²]",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "square_both_sides",
            name: "Square Both Sides",
            type: "steps",
            description: "Square both sides to eliminate square roots",
            value: "Step 3: x² + y² = (x-4)² + y²",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "expand_right_side",
            name: "Expand Right Side",
            type: "steps",
            description: "Expand the right side of the equation",
            value: "Step 4: x² + y² = x² - 8x + 16 + y²",
            importance: 10,
            x: 0, y: 150, z: 20
          },
          {
            id: "solve_equation",
            name: "Solve Equation",
            type: "steps",
            description: "Simplify and solve for x",
            value: "Step 5: x² = x² - 8x + 16 → 8x = 16 → x = 2",
            importance: 10,
            x: 0, y: 200, z: 20
          },
          {
            id: "simplification",
            name: "Simplification",
            type: "values",
            description: "Square both sides and simplify",
            value: "x² + y² = (x-4)² + y², x² = x² - 8x + 16",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The locus equation",
            value: "x = 2",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "point_a", to: "distance_formula", type: "used in", strength: 9 },
          { from: "point_b", to: "distance_formula", type: "used in", strength: 9 },
          { from: "equidistant_condition", to: "distance_formula", type: "gives", strength: 10 },
          { from: "distance_formula", to: "simplification", type: "gives", strength: 10 },
          { from: "simplification", to: "result", type: "equals", strength: 10 },
          { from: "perpendicular_bisector_theorem", to: "result", type: "gives", strength: 10 },
          { from: "midpoint_formula", to: "perpendicular_bisector_formula", type: "gives", strength: 9 },
          { from: "perpendicular_bisector_formula", to: "result", type: "gives", strength: 10 },
          { from: "identify_points", to: "write_distance_equation", type: "enables", strength: 8 },
          { from: "write_distance_equation", to: "square_both_sides", type: "enables", strength: 9 },
          { from: "square_both_sides", to: "expand_right_side", type: "enables", strength: 10 },
          { from: "expand_right_side", to: "solve_equation", type: "enables", strength: 10 },
          { from: "solve_equation", to: "result", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'locus_fund_02',
      problem: "Find the locus of points at a distance of 3 units from the point (2, 1)",
      problemType: "locus",
      difficulty: "fundamental",
      description: "Circle locus",
      graphData: {
        nodes: [
          {
            id: "center",
            name: "Center",
            type: "concepts",
            description: "Fixed point",
            value: "(2, 1)",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "distance",
            name: "Distance",
            type: "concepts",
            description: "Fixed distance",
            value: "3 units",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "circle_equation_formula",
            name: "Circle Equation Formula",
            type: "formulas",
            description: "Standard form of circle equation: (x-h)² + (y-k)² = r²",
            value: "(x-h)² + (y-k)² = r²",
            importance: 10,
            x: 0, y: 0, z: 40
          },
          {
            id: "distance_formula",
            name: "Distance Formula",
            type: "formulas",
            description: "Distance between points: √[(x-x₁)² + (y-y₁)²]",
            value: "√[(x-2)² + (y-1)²] = 3",
            importance: 10,
            x: 0, y: 50, z: 25
          },
          {
            id: "circle_equation",
            name: "Circle Equation",
            type: "formulas",
            description: "Equation of circle: (x-h)² + (y-k)² = r²",
            value: "(x-2)² + (y-1)² = 9",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "circle_definition",
            name: "Circle Definition",
            type: "formulas",
            description: "Circle is locus of points at fixed distance from center",
            value: "All points at distance r from center (h,k)",
            importance: 9,
            x: 0, y: 100, z: 40
          },
          {
            id: "step1_identify",
            name: "Step 1: Identify",
            type: "steps",
            description: "Recognize the center and radius",
            value: "Step 1: Center (2, 1), radius = 3",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "step2_apply_formula",
            name: "Step 2: Apply Formula",
            type: "steps",
            description: "Use circle equation formula",
            value: "Step 2: (x-2)² + (y-1)² = 3²",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "step3_simplify",
            name: "Step 3: Simplify",
            type: "steps",
            description: "Simplify the equation",
            value: "Step 3: (x-2)² + (y-1)² = 9",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "step4_verify",
            name: "Step 4: Verify",
            type: "steps",
            description: "Verify the equation represents a circle",
            value: "Step 4: This is standard circle form with center (2,1) and radius 3",
            importance: 9,
            x: 0, y: 150, z: 20
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The locus equation",
            value: "(x-2)² + (y-1)² = 9",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "center", to: "circle_equation", type: "used in", strength: 9 },
          { from: "distance", to: "circle_equation", type: "used in", strength: 9 },
          { from: "circle_equation_formula", to: "circle_equation", type: "gives", strength: 10 },
          { from: "distance_formula", to: "circle_equation", type: "gives", strength: 10 },
          { from: "circle_definition", to: "circle_equation", type: "gives", strength: 9 },
          { from: "circle_equation", to: "result", type: "equals", strength: 10 },
          { from: "step1_identify", to: "step2_apply_formula", type: "enables", strength: 8 },
          { from: "step2_apply_formula", to: "step3_simplify", type: "enables", strength: 9 },
          { from: "step3_simplify", to: "step4_verify", type: "enables", strength: 10 },
          { from: "step4_verify", to: "result", type: "equals", strength: 9 }
        ]
      }
    },
    {
      id: 'locus_fund_03',
      problem: "Find the locus of points equidistant from the x-axis and y-axis",
      problemType: "locus",
      difficulty: "fundamental",
      description: "Distance from coordinate axes",
      graphData: {
        nodes: [
          {
            id: "x_axis",
            name: "X-Axis",
            type: "concepts",
            description: "Line y = 0",
            value: "y = 0",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "y_axis",
            name: "Y-Axis",
            type: "concepts",
            description: "Line x = 0",
            value: "x = 0",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "distance_from_line_formula",
            name: "Distance from Line Formula",
            type: "formulas",
            description: "Distance from line ax + by + c = 0: |ax + by + c|/√(a² + b²)",
            value: "Distance from x-axis = |y|, Distance from y-axis = |x|",
            importance: 10,
            x: 0, y: 0, z: 40
          },
          {
            id: "distance_formula",
            name: "Distance Formula",
            type: "formulas",
            description: "Distance from line: |ax + by + c|/√(a² + b²)",
            value: "Distance from x-axis = |y|, Distance from y-axis = |x|",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "distance_condition",
            name: "Distance Condition",
            type: "concepts",
            description: "Distance from x-axis = Distance from y-axis",
            value: "|y| = |x|",
            importance: 10,
            x: 150, y: -100, z: 20
          },
          {
            id: "absolute_value_property",
            name: "Absolute Value Property",
            type: "formulas",
            description: "|a| = |b| implies a = ±b",
            value: "If |y| = |x|, then y = ±x",
            importance: 9,
            x: 0, y: 50, z: 40
          },
          {
            id: "angle_bisector_theorem",
            name: "Angle Bisector Theorem",
            type: "formulas",
            description: "Points equidistant from two lines form angle bisectors",
            value: "Equidistant from axes → angle bisectors y = ±x",
            importance: 10,
            x: 0, y: 100, z: 40
          },
          {
            id: "step1_identify",
            name: "Step 1: Identify",
            type: "steps",
            description: "Recognize the axes and condition",
            value: "Step 1: x-axis (y=0), y-axis (x=0), equidistant condition",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "step2_apply_distance",
            name: "Step 2: Apply Distance",
            type: "steps",
            description: "Use distance formula for both axes",
            value: "Step 2: |y| = |x|",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "step3_solve",
            name: "Step 3: Solve",
            type: "steps",
            description: "Solve the absolute value equation",
            value: "Step 3: y = ±x",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "step4_interpret",
            name: "Step 4: Interpret",
            type: "steps",
            description: "Interpret the geometric meaning",
            value: "Step 4: These are the angle bisectors of the coordinate axes",
            importance: 9,
            x: 0, y: 150, z: 20
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The locus equation",
            value: "y = ±x",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "x_axis", to: "distance_condition", type: "used in", strength: 9 },
          { from: "y_axis", to: "distance_condition", type: "used in", strength: 9 },
          { from: "distance_from_line_formula", to: "distance_condition", type: "gives", strength: 10 },
          { from: "distance_formula", to: "distance_condition", type: "gives", strength: 10 },
          { from: "distance_condition", to: "result", type: "equals", strength: 10 },
          { from: "absolute_value_property", to: "result", type: "gives", strength: 9 },
          { from: "angle_bisector_theorem", to: "result", type: "gives", strength: 10 },
          { from: "step1_identify", to: "step2_apply_distance", type: "enables", strength: 8 },
          { from: "step2_apply_distance", to: "step3_solve", type: "enables", strength: 9 },
          { from: "step3_solve", to: "step4_interpret", type: "enables", strength: 10 },
          { from: "step4_interpret", to: "result", type: "equals", strength: 9 }
        ]
      }
    },
    {
      id: 'locus_fund_04',
      problem: "Find the locus of points at a distance of 2 units from the line x = 3",
      problemType: "locus",
      difficulty: "fundamental",
      description: "Distance from vertical line",
      graphData: {
        nodes: [
          {
            id: "line",
            name: "Line",
            type: "concepts",
            description: "Vertical line",
            value: "x = 3",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "distance",
            name: "Distance",
            type: "concepts",
            description: "Fixed distance",
            value: "2 units",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "distance_from_vertical_line",
            name: "Distance from Vertical Line",
            type: "formulas",
            description: "Distance from vertical line x = a: |x - a|",
            value: "Distance from x = 3: |x - 3|",
            importance: 10,
            x: 0, y: 0, z: 40
          },
          {
            id: "distance_formula",
            name: "Distance Formula",
            type: "formulas",
            description: "Distance from vertical line: |x - a| = d",
            value: "|x - 3| = 2",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "absolute_value_equation",
            name: "Absolute Value Equation",
            type: "formulas",
            description: "|x - a| = d implies x - a = ±d",
            value: "|x - 3| = 2 implies x - 3 = ±2",
            importance: 9,
            x: 0, y: 50, z: 40
          },
          {
            id: "parallel_lines_theorem",
            name: "Parallel Lines Theorem",
            type: "formulas",
            description: "Points at fixed distance from line form parallel lines",
            value: "Distance d from x = a → x = a ± d",
            importance: 10,
            x: 0, y: 100, z: 40
          },
          {
            id: "step1_identify",
            name: "Step 1: Identify",
            type: "steps",
            description: "Recognize the line and distance",
            value: "Step 1: Line x = 3, distance = 2 units",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "step2_apply_formula",
            name: "Step 2: Apply Formula",
            type: "steps",
            description: "Use distance formula for vertical line",
            value: "Step 2: |x - 3| = 2",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "step3_solve",
            name: "Step 3: Solve",
            type: "steps",
            description: "Solve the absolute value equation",
            value: "Step 3: x - 3 = ±2 → x = 1 or x = 5",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "step4_interpret",
            name: "Step 4: Interpret",
            type: "steps",
            description: "Interpret the geometric meaning",
            value: "Step 4: Two parallel vertical lines at distance 2 from x = 3",
            importance: 9,
            x: 0, y: 150, z: 20
          },
          {
            id: "solution",
            name: "Solution",
            type: "values",
            description: "Solve absolute value equation",
            value: "x - 3 = ±2, x = 1 or x = 5",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The locus equations",
            value: "x = 1 or x = 5",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "line", to: "distance_formula", type: "used in", strength: 9 },
          { from: "distance", to: "distance_formula", type: "used in", strength: 9 },
          { from: "distance_from_vertical_line", to: "distance_formula", type: "gives", strength: 10 },
          { from: "distance_formula", to: "solution", type: "gives", strength: 10 },
          { from: "absolute_value_equation", to: "solution", type: "gives", strength: 9 },
          { from: "parallel_lines_theorem", to: "result", type: "gives", strength: 10 },
          { from: "solution", to: "result", type: "equals", strength: 10 },
          { from: "step1_identify", to: "step2_apply_formula", type: "enables", strength: 8 },
          { from: "step2_apply_formula", to: "step3_solve", type: "enables", strength: 9 },
          { from: "step3_solve", to: "step4_interpret", type: "enables", strength: 10 },
          { from: "step4_interpret", to: "result", type: "equals", strength: 9 }
        ]
      }
    },
    {
      id: 'locus_fund_05',
      problem: "Find the locus of points equidistant from the lines y = 2 and y = -2",
      problemType: "locus",
      difficulty: "fundamental",
      description: "Equidistant from parallel lines",
      graphData: {
        nodes: [
          {
            id: "line1",
            name: "Line 1",
            type: "concepts",
            description: "First horizontal line",
            value: "y = 2",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "line2",
            name: "Line 2",
            type: "concepts",
            description: "Second horizontal line",
            value: "y = -2",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "distance_from_horizontal_line",
            name: "Distance from Horizontal Line",
            type: "formulas",
            description: "Distance from horizontal line y = k: |y - k|",
            value: "Distance from y = 2: |y - 2|, Distance from y = -2: |y + 2|",
            importance: 10,
            x: 0, y: 0, z: 40
          },
          {
            id: "distance_formula",
            name: "Distance Formula",
            type: "formulas",
            description: "Distance from horizontal line: |y - k| = d",
            value: "Distance from y=2: |y-2|, Distance from y=-2: |y+2|",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "equidistant_condition",
            name: "Equidistant Condition",
            type: "concepts",
            description: "Distance from line1 = Distance from line2",
            value: "|y - 2| = |y + 2|",
            importance: 10,
            x: 150, y: -100, z: 20
          },
          {
            id: "absolute_value_property",
            name: "Absolute Value Property",
            type: "formulas",
            description: "|a| = |b| implies a = ±b",
            value: "If |y - 2| = |y + 2|, then y - 2 = ±(y + 2)",
            importance: 9,
            x: 0, y: 50, z: 40
          },
          {
            id: "midline_theorem",
            name: "Midline Theorem",
            type: "formulas",
            description: "Points equidistant from parallel lines lie on midline",
            value: "Equidistant from y = k₁ and y = k₂ → y = (k₁ + k₂)/2",
            importance: 10,
            x: 0, y: 100, z: 40
          },
          {
            id: "step1_identify",
            name: "Step 1: Identify",
            type: "steps",
            description: "Recognize the parallel lines",
            value: "Step 1: Line 1 (y=2), Line 2 (y=-2), equidistant condition",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "step2_apply_distance",
            name: "Step 2: Apply Distance",
            type: "steps",
            description: "Use distance formula for both lines",
            value: "Step 2: |y - 2| = |y + 2|",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "step3_solve",
            name: "Step 3: Solve",
            type: "steps",
            description: "Solve the absolute value equation",
            value: "Step 3: y - 2 = ±(y + 2) → y = 0",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "step4_verify",
            name: "Step 4: Verify",
            type: "steps",
            description: "Verify using midline theorem",
            value: "Step 4: Midline y = (2 + (-2))/2 = 0",
            importance: 9,
            x: 0, y: 150, z: 20
          },
          {
            id: "solution",
            name: "Solution",
            type: "values",
            description: "Solve absolute value equation",
            value: "y - 2 = ±(y + 2), y = 0",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The locus equation",
            value: "y = 0",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "line1", to: "equidistant_condition", type: "used in", strength: 9 },
          { from: "line2", to: "equidistant_condition", type: "used in", strength: 9 },
          { from: "distance_from_horizontal_line", to: "equidistant_condition", type: "gives", strength: 10 },
          { from: "distance_formula", to: "equidistant_condition", type: "gives", strength: 10 },
          { from: "equidistant_condition", to: "solution", type: "gives", strength: 10 },
          { from: "absolute_value_property", to: "solution", type: "gives", strength: 9 },
          { from: "midline_theorem", to: "result", type: "gives", strength: 10 },
          { from: "solution", to: "result", type: "equals", strength: 10 },
          { from: "step1_identify", to: "step2_apply_distance", type: "enables", strength: 8 },
          { from: "step2_apply_distance", to: "step3_solve", type: "enables", strength: 9 },
          { from: "step3_solve", to: "step4_verify", type: "enables", strength: 10 },
          { from: "step4_verify", to: "result", type: "equals", strength: 9 }
        ]
      }
    }
  ],
  
  intermediate: [
    {
      id: 'locus_inter_01',
      problem: "Find the locus of points whose distance from (0, 0) is twice the distance from (3, 0)",
      problemType: "locus",
      difficulty: "intermediate",
      description: "Ratio of distances",
      graphData: {
        nodes: [
          {
            id: "point1",
            name: "Point 1",
            type: "concepts",
            description: "First fixed point",
            value: "(0, 0)",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "point2",
            name: "Point 2",
            type: "concepts",
            description: "Second fixed point",
            value: "(3, 0)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "distance_formula",
            name: "Distance Formula",
            type: "formulas",
            description: "Distance between points: √[(x-x₁)² + (y-y₁)²]",
            value: "Distance from (0,0): √(x² + y²), Distance from (3,0): √[(x-3)² + y²]",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "ratio_condition",
            name: "Ratio Condition",
            type: "concepts",
            description: "Distance from point1 = 2 × Distance from point2",
            value: "√(x² + y²) = 2√[(x-3)² + y²]",
            importance: 10,
            x: 150, y: -100, z: 20
          },
          {
            id: "ratio_of_distances_theorem",
            name: "Ratio of Distances Theorem",
            type: "formulas",
            description: "Ratio of distances from two points forms a circle",
            value: "PA = k·PB → circle (Apollonius circle)",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "apollonius_circle_formula",
            name: "Apollonius Circle Formula",
            type: "formulas",
            description: "Circle with ratio k: (x² + y²) = k²[(x-a)² + y²]",
            value: "For ratio 2:1, k = 2",
            importance: 9,
            x: 0, y: 100, z: 40
          },
          {
            id: "step1_identify",
            name: "Step 1: Identify",
            type: "steps",
            description: "Recognize the points and ratio condition",
            value: "Step 1: Point 1 (0,0), Point 2 (3,0), ratio = 2:1",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "step2_write_equation",
            name: "Step 2: Write Equation",
            type: "steps",
            description: "Set up the ratio equation",
            value: "Step 2: √(x² + y²) = 2√[(x-3)² + y²]",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "step3_square_both",
            name: "Step 3: Square Both",
            type: "steps",
            description: "Square both sides to eliminate square roots",
            value: "Step 3: x² + y² = 4[(x-3)² + y²]",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "step4_expand",
            name: "Step 4: Expand",
            type: "steps",
            description: "Expand and simplify",
            value: "Step 4: x² + y² = 4(x² - 6x + 9 + y²)",
            importance: 10,
            x: 0, y: 150, z: 20
          },
          {
            id: "step5_simplify",
            name: "Step 5: Simplify",
            type: "steps",
            description: "Collect like terms and simplify",
            value: "Step 5: 3x² + 3y² - 24x + 36 = 0",
            importance: 10,
            x: 0, y: 200, z: 20
          },
          {
            id: "step6_complete_square",
            name: "Step 6: Complete Square",
            type: "steps",
            description: "Complete the square to find center and radius",
            value: "Step 6: (x-4)² + y² = 4",
            importance: 10,
            x: 0, y: 250, z: 20
          },
          {
            id: "simplification",
            name: "Simplification",
            type: "values",
            description: "Square both sides and expand",
            value: "x² + y² = 4[(x-3)² + y²]",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The locus equation",
            value: "(x-4)² + y² = 4",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "point1", to: "ratio_condition", type: "used in", strength: 9 },
          { from: "point2", to: "ratio_condition", type: "used in", strength: 9 },
          { from: "distance_formula", to: "ratio_condition", type: "gives", strength: 10 },
          { from: "ratio_condition", to: "simplification", type: "gives", strength: 10 },
          { from: "ratio_of_distances_theorem", to: "result", type: "gives", strength: 10 },
          { from: "apollonius_circle_formula", to: "result", type: "gives", strength: 9 },
          { from: "simplification", to: "result", type: "equals", strength: 10 },
          { from: "step1_identify", to: "step2_write_equation", type: "enables", strength: 8 },
          { from: "step2_write_equation", to: "step3_square_both", type: "enables", strength: 9 },
          { from: "step3_square_both", to: "step4_expand", type: "enables", strength: 10 },
          { from: "step4_expand", to: "step5_simplify", type: "enables", strength: 10 },
          { from: "step5_simplify", to: "step6_complete_square", type: "enables", strength: 10 },
          { from: "step6_complete_square", to: "result", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'locus_inter_02',
      problem: "Find the locus of points equidistant from the point (2, 0) and the line x = -2",
      problemType: "locus",
      difficulty: "intermediate",
      description: "Parabola locus",
      graphData: {
        nodes: [
          {
            id: "point",
            name: "Point",
            type: "concepts",
            description: "Fixed point",
            value: "(2, 0)",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "line",
            name: "Line",
            type: "concepts",
            description: "Directrix",
            value: "x = -2",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "parabola_definition",
            name: "Parabola Definition",
            type: "formulas",
            description: "Parabola is locus of points equidistant from focus and directrix",
            value: "Distance from focus = Distance from directrix",
            importance: 10,
            x: 0, y: 0, z: 40
          },
          {
            id: "parabola_formula",
            name: "Parabola Formula",
            type: "formulas",
            description: "Parabola: equidistant from focus and directrix",
            value: "Distance from focus = Distance from directrix",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "equidistant_condition",
            name: "Equidistant Condition",
            type: "concepts",
            description: "Distance from point = Distance from line",
            value: "√[(x-2)² + y²] = |x + 2|",
            importance: 10,
            x: 150, y: -100, z: 20
          },
          {
            id: "parabola_standard_form",
            name: "Parabola Standard Form",
            type: "formulas",
            description: "Standard form: y² = 4px (focus at (p,0), directrix x = -p)",
            value: "For focus (2,0) and directrix x = -2, p = 2",
            importance: 9,
            x: 0, y: 50, z: 40
          },
          {
            id: "focus_directrix_theorem",
            name: "Focus-Directrix Theorem",
            type: "formulas",
            description: "Parabola with focus (p,0) and directrix x = -p has equation y² = 4px",
            value: "Focus (2,0), directrix x = -2 → y² = 8x",
            importance: 10,
            x: 0, y: 100, z: 40
          },
          {
            id: "step1_identify",
            name: "Step 1: Identify",
            type: "steps",
            description: "Recognize focus and directrix",
            value: "Step 1: Focus (2,0), Directrix x = -2",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "step2_write_equation",
            name: "Step 2: Write Equation",
            type: "steps",
            description: "Set up equidistant equation",
            value: "Step 2: √[(x-2)² + y²] = |x + 2|",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "step3_square_both",
            name: "Step 3: Square Both",
            type: "steps",
            description: "Square both sides to eliminate square root",
            value: "Step 3: (x-2)² + y² = (x+2)²",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "step4_expand",
            name: "Step 4: Expand",
            type: "steps",
            description: "Expand both sides",
            value: "Step 4: x² - 4x + 4 + y² = x² + 4x + 4",
            importance: 10,
            x: 0, y: 150, z: 20
          },
          {
            id: "step5_simplify",
            name: "Step 5: Simplify",
            type: "steps",
            description: "Simplify and solve for y²",
            value: "Step 5: y² = 8x",
            importance: 10,
            x: 0, y: 200, z: 20
          },
          {
            id: "step6_verify",
            name: "Step 6: Verify",
            type: "steps",
            description: "Verify using standard form",
            value: "Step 6: y² = 4px with p = 2 → y² = 8x",
            importance: 9,
            x: 0, y: 250, z: 20
          },
          {
            id: "simplification",
            name: "Simplification",
            type: "values",
            description: "Square both sides and expand",
            value: "(x-2)² + y² = (x+2)²",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The locus equation",
            value: "y² = 8x",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "point", to: "equidistant_condition", type: "used in", strength: 9 },
          { from: "line", to: "equidistant_condition", type: "used in", strength: 9 },
          { from: "parabola_definition", to: "equidistant_condition", type: "gives", strength: 10 },
          { from: "parabola_formula", to: "equidistant_condition", type: "gives", strength: 10 },
          { from: "equidistant_condition", to: "simplification", type: "gives", strength: 10 },
          { from: "parabola_standard_form", to: "result", type: "gives", strength: 9 },
          { from: "focus_directrix_theorem", to: "result", type: "gives", strength: 10 },
          { from: "simplification", to: "result", type: "equals", strength: 10 },
          { from: "step1_identify", to: "step2_write_equation", type: "enables", strength: 8 },
          { from: "step2_write_equation", to: "step3_square_both", type: "enables", strength: 9 },
          { from: "step3_square_both", to: "step4_expand", type: "enables", strength: 10 },
          { from: "step4_expand", to: "step5_simplify", type: "enables", strength: 10 },
          { from: "step5_simplify", to: "step6_verify", type: "enables", strength: 10 },
          { from: "step6_verify", to: "result", type: "equals", strength: 9 }
        ]
      }
    },
    {
      id: 'locus_inter_03',
      problem: "Find the locus of points whose sum of distances from (3, 0) and (-3, 0) is 10",
      problemType: "locus",
      difficulty: "intermediate",
      description: "Ellipse locus",
      graphData: {
        nodes: [
          {
            id: "focus1",
            name: "Focus 1",
            type: "concepts",
            description: "First focus",
            value: "(3, 0)",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "focus2",
            name: "Focus 2",
            type: "concepts",
            description: "Second focus",
            value: "(-3, 0)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "ellipse_definition",
            name: "Ellipse Definition",
            type: "formulas",
            description: "Ellipse is locus of points with constant sum of distances from foci",
            value: "Sum of distances from foci = constant",
            importance: 10,
            x: 0, y: 0, z: 40
          },
          {
            id: "ellipse_formula",
            name: "Ellipse Formula",
            type: "formulas",
            description: "Ellipse: sum of distances from foci = constant",
            value: "Sum of distances from foci = 2a (major axis)",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "sum_condition",
            name: "Sum Condition",
            type: "concepts",
            description: "Sum of distances = 10",
            value: "√[(x-3)² + y²] + √[(x+3)² + y²] = 10",
            importance: 10,
            x: 150, y: -100, z: 20
          },
          {
            id: "ellipse_standard_form",
            name: "Ellipse Standard Form",
            type: "formulas",
            description: "Standard form: x²/a² + y²/b² = 1, where c² = a² - b²",
            value: "For foci (±3,0), c = 3, a = 5, b² = a² - c² = 16",
            importance: 9,
            x: 0, y: 50, z: 40
          },
          {
            id: "ellipse_parameters",
            name: "Ellipse Parameters",
            type: "formulas",
            description: "For foci (±c,0), sum = 2a: x²/a² + y²/b² = 1 where b² = a² - c²",
            value: "a = 5, c = 3, b² = 25 - 9 = 16",
            importance: 10,
            x: 0, y: 100, z: 40
          },
          {
            id: "step1_identify",
            name: "Step 1: Identify",
            type: "steps",
            description: "Recognize the foci and constant sum",
            value: "Step 1: Focus 1 (3,0), Focus 2 (-3,0), sum = 10",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "step2_write_equation",
            name: "Step 2: Write Equation",
            type: "steps",
            description: "Set up the sum equation",
            value: "Step 2: √[(x-3)² + y²] + √[(x+3)² + y²] = 10",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "step3_apply_ellipse",
            name: "Step 3: Apply Ellipse",
            type: "steps",
            description: "Use ellipse standard form",
            value: "Step 3: x²/a² + y²/b² = 1, where 2a = 10",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "step4_calculate",
            name: "Step 4: Calculate",
            type: "steps",
            description: "Calculate a and b values",
            value: "Step 4: a = 5, c = 3, b² = a² - c² = 16",
            importance: 10,
            x: 0, y: 150, z: 20
          },
          {
            id: "step5_write_result",
            name: "Step 5: Write Result",
            type: "steps",
            description: "Write the final equation",
            value: "Step 5: x²/25 + y²/16 = 1",
            importance: 10,
            x: 0, y: 200, z: 20
          },
          {
            id: "step6_verify",
            name: "Step 6: Verify",
            type: "steps",
            description: "Verify the ellipse properties",
            value: "Step 6: Center (0,0), major axis 10, minor axis 8",
            importance: 9,
            x: 0, y: 250, z: 20
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The locus equation",
            value: "x²/25 + y²/16 = 1",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "focus1", to: "sum_condition", type: "used in", strength: 9 },
          { from: "focus2", to: "sum_condition", type: "used in", strength: 9 },
          { from: "ellipse_definition", to: "sum_condition", type: "gives", strength: 10 },
          { from: "ellipse_formula", to: "sum_condition", type: "gives", strength: 10 },
          { from: "sum_condition", to: "result", type: "gives", strength: 10 },
          { from: "ellipse_standard_form", to: "result", type: "gives", strength: 9 },
          { from: "ellipse_parameters", to: "result", type: "gives", strength: 10 },
          { from: "step1_identify", to: "step2_write_equation", type: "enables", strength: 8 },
          { from: "step2_write_equation", to: "step3_apply_ellipse", type: "enables", strength: 9 },
          { from: "step3_apply_ellipse", to: "step4_calculate", type: "enables", strength: 10 },
          { from: "step4_calculate", to: "step5_write_result", type: "enables", strength: 10 },
          { from: "step5_write_result", to: "step6_verify", type: "enables", strength: 10 },
          { from: "step6_verify", to: "result", type: "equals", strength: 9 }
        ]
      }
    },
    {
      id: 'locus_inter_04',
      problem: "Find the locus of points whose difference of distances from (4, 0) and (-4, 0) is 6",
      problemType: "locus",
      difficulty: "intermediate",
      description: "Hyperbola locus",
      graphData: {
        nodes: [
          {
            id: "focus1",
            name: "Focus 1",
            type: "concepts",
            description: "First focus",
            value: "(4, 0)",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "focus2",
            name: "Focus 2",
            type: "concepts",
            description: "Second focus",
            value: "(-4, 0)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "hyperbola_definition",
            name: "Hyperbola Definition",
            type: "formulas",
            description: "Hyperbola is locus of points with constant difference of distances from foci",
            value: "|Difference of distances from foci| = constant",
            importance: 10,
            x: 0, y: 0, z: 40
          },
          {
            id: "hyperbola_formula",
            name: "Hyperbola Formula",
            type: "formulas",
            description: "Hyperbola: difference of distances from foci = constant",
            value: "|Difference of distances from foci| = 2a (transverse axis)",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "difference_condition",
            name: "Difference Condition",
            type: "concepts",
            description: "Difference of distances = 6",
            value: "|√[(x-4)² + y²] - √[(x+4)² + y²]| = 6",
            importance: 10,
            x: 150, y: -100, z: 20
          },
          {
            id: "hyperbola_standard_form",
            name: "Hyperbola Standard Form",
            type: "formulas",
            description: "Standard form: x²/a² - y²/b² = 1, where c² = a² + b²",
            value: "For foci (±4,0), c = 4, a = 3, b² = c² - a² = 7",
            importance: 9,
            x: 0, y: 50, z: 40
          },
          {
            id: "hyperbola_parameters",
            name: "Hyperbola Parameters",
            type: "formulas",
            description: "For foci (±c,0), difference = 2a: x²/a² - y²/b² = 1 where b² = c² - a²",
            value: "a = 3, c = 4, b² = 16 - 9 = 7",
            importance: 10,
            x: 0, y: 100, z: 40
          },
          {
            id: "step1_identify",
            name: "Step 1: Identify",
            type: "steps",
            description: "Recognize the foci and constant difference",
            value: "Step 1: Focus 1 (4,0), Focus 2 (-4,0), difference = 6",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "step2_write_equation",
            name: "Step 2: Write Equation",
            type: "steps",
            description: "Set up the difference equation",
            value: "Step 2: |√[(x-4)² + y²] - √[(x+4)² + y²]| = 6",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "step3_apply_hyperbola",
            name: "Step 3: Apply Hyperbola",
            type: "steps",
            description: "Use hyperbola standard form",
            value: "Step 3: x²/a² - y²/b² = 1, where 2a = 6",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "step4_calculate",
            name: "Step 4: Calculate",
            type: "steps",
            description: "Calculate a and b values",
            value: "Step 4: a = 3, c = 4, b² = c² - a² = 7",
            importance: 10,
            x: 0, y: 150, z: 20
          },
          {
            id: "step5_write_result",
            name: "Step 5: Write Result",
            type: "steps",
            description: "Write the final equation",
            value: "Step 5: x²/9 - y²/7 = 1",
            importance: 10,
            x: 0, y: 200, z: 20
          },
          {
            id: "step6_verify",
            name: "Step 6: Verify",
            type: "steps",
            description: "Verify the hyperbola properties",
            value: "Step 6: Center (0,0), transverse axis 6, conjugate axis 2√7",
            importance: 9,
            x: 0, y: 250, z: 20
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The locus equation",
            value: "x²/9 - y²/7 = 1",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "focus1", to: "difference_condition", type: "used in", strength: 9 },
          { from: "focus2", to: "difference_condition", type: "used in", strength: 9 },
          { from: "hyperbola_definition", to: "difference_condition", type: "gives", strength: 10 },
          { from: "hyperbola_formula", to: "difference_condition", type: "gives", strength: 10 },
          { from: "difference_condition", to: "result", type: "gives", strength: 10 },
          { from: "hyperbola_standard_form", to: "result", type: "gives", strength: 9 },
          { from: "hyperbola_parameters", to: "result", type: "gives", strength: 10 },
          { from: "step1_identify", to: "step2_write_equation", type: "enables", strength: 8 },
          { from: "step2_write_equation", to: "step3_apply_hyperbola", type: "enables", strength: 9 },
          { from: "step3_apply_hyperbola", to: "step4_calculate", type: "enables", strength: 10 },
          { from: "step4_calculate", to: "step5_write_result", type: "enables", strength: 10 },
          { from: "step5_write_result", to: "step6_verify", type: "enables", strength: 10 },
          { from: "step6_verify", to: "result", type: "equals", strength: 9 }
        ]
      }
    },
    {
      id: 'locus_inter_05',
      problem: "Find the locus of points equidistant from the circle x² + y² = 16 and the point (4, 0)",
      problemType: "locus",
      difficulty: "intermediate",
      description: "Complex locus with circle",
      graphData: {
        nodes: [
          {
            id: "circle",
            name: "Circle",
            type: "concepts",
            description: "Given circle",
            value: "x² + y² = 16",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "point",
            name: "Point",
            type: "concepts",
            description: "Fixed point",
            value: "(4, 0)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "distance_from_circle",
            name: "Distance from Circle",
            type: "formulas",
            description: "Distance from circle center: |√(x² + y²) - r|",
            value: "Distance from circle = |√(x² + y²) - 4|",
            importance: 10,
            x: 0, y: 0, z: 40
          },
          {
            id: "distance_formula",
            name: "Distance Formula",
            type: "formulas",
            description: "Distance from circle: |√(x² + y²) - r|, Distance from point: √[(x-x₁)² + (y-y₁)²]",
            value: "Distance from circle = |√(x² + y²) - 4|, Distance from point = √[(x-4)² + y²]",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "equidistant_condition",
            name: "Equidistant Condition",
            type: "concepts",
            description: "Distance from circle = Distance from point",
            value: "|√(x² + y²) - 4| = √[(x-4)² + y²]",
            importance: 10,
            x: 150, y: -100, z: 20
          },
          {
            id: "circle_point_theorem",
            name: "Circle-Point Theorem",
            type: "formulas",
            description: "Points equidistant from circle and point lie on perpendicular bisector",
            value: "Equidistant from circle and point → perpendicular bisector",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "perpendicular_bisector_formula",
            name: "Perpendicular Bisector Formula",
            type: "formulas",
            description: "Perpendicular bisector of segment from circle center to point",
            value: "Midpoint = (2,0), perpendicular bisector: x = 2",
            importance: 9,
            x: 0, y: 100, z: 40
          },
          {
            id: "step1_identify",
            name: "Step 1: Identify",
            type: "steps",
            description: "Recognize circle and point",
            value: "Step 1: Circle x² + y² = 16 (radius 4), Point (4,0)",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "step2_write_equation",
            name: "Step 2: Write Equation",
            type: "steps",
            description: "Set up equidistant equation",
            value: "Step 2: |√(x² + y²) - 4| = √[(x-4)² + y²]",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "step3_analyze",
            name: "Step 3: Analyze",
            type: "steps",
            description: "Analyze the geometric meaning",
            value: "Step 3: Points equidistant from circle and point form a line",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "step4_apply_theorem",
            name: "Step 4: Apply Theorem",
            type: "steps",
            description: "Use circle-point theorem",
            value: "Step 4: Perpendicular bisector of segment from (0,0) to (4,0)",
            importance: 10,
            x: 0, y: 150, z: 20
          },
          {
            id: "step5_solve",
            name: "Step 5: Solve",
            type: "steps",
            description: "Solve the equation",
            value: "Step 5: x = 2 (vertical line)",
            importance: 10,
            x: 0, y: 200, z: 20
          },
          {
            id: "step6_verify",
            name: "Step 6: Verify",
            type: "steps",
            description: "Verify the solution",
            value: "Step 6: Check that x = 2 satisfies the original equation",
            importance: 9,
            x: 0, y: 250, z: 20
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The locus equation",
            value: "x = 2",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "circle", to: "equidistant_condition", type: "used in", strength: 9 },
          { from: "point", to: "equidistant_condition", type: "used in", strength: 9 },
          { from: "distance_from_circle", to: "equidistant_condition", type: "gives", strength: 10 },
          { from: "distance_formula", to: "equidistant_condition", type: "gives", strength: 10 },
          { from: "equidistant_condition", to: "result", type: "gives", strength: 10 },
          { from: "circle_point_theorem", to: "result", type: "gives", strength: 10 },
          { from: "perpendicular_bisector_formula", to: "result", type: "gives", strength: 9 },
          { from: "step1_identify", to: "step2_write_equation", type: "enables", strength: 8 },
          { from: "step2_write_equation", to: "step3_analyze", type: "enables", strength: 9 },
          { from: "step3_analyze", to: "step4_apply_theorem", type: "enables", strength: 10 },
          { from: "step4_apply_theorem", to: "step5_solve", type: "enables", strength: 10 },
          { from: "step5_solve", to: "step6_verify", type: "enables", strength: 10 },
          { from: "step6_verify", to: "result", type: "equals", strength: 9 }
        ]
      }
    }
  ],
  
  harder: [
    {
      id: 'locus_hard_01',
      problem: "Find the locus of points whose distance from the line 3x + 4y = 12 is equal to the distance from the point (0, 0)",
      problemType: "locus",
      difficulty: "harder",
      description: "Complex locus with line and point",
      graphData: {
        nodes: [
          {
            id: "line",
            name: "Line",
            type: "concepts",
            description: "Given line",
            value: "3x + 4y = 12",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "point",
            name: "Point",
            type: "concepts",
            description: "Fixed point",
            value: "(0, 0)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "distance_from_line_formula",
            name: "Distance from Line Formula",
            type: "formulas",
            description: "Distance from line ax + by + c = 0: |ax + by + c|/√(a² + b²)",
            value: "Distance from 3x + 4y = 12: |3x + 4y - 12|/5",
            importance: 10,
            x: 0, y: 0, z: 40
          },
          {
            id: "distance_formula",
            name: "Distance Formula",
            type: "formulas",
            description: "Distance from line: |ax + by + c|/√(a² + b²), Distance from point: √(x² + y²)",
            value: "Distance from line = |3x + 4y - 12|/5, Distance from point = √(x² + y²)",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "equidistant_condition",
            name: "Equidistant Condition",
            type: "concepts",
            description: "Distance from line = Distance from point",
            value: "|3x + 4y - 12|/5 = √(x² + y²)",
            importance: 10,
            x: 150, y: -100, z: 20
          },
          {
            id: "parabola_general_form",
            name: "Parabola General Form",
            type: "formulas",
            description: "General form of parabola: Ax² + By² + Cxy + Dx + Ey + F = 0",
            value: "This will result in a general conic section",
            importance: 9,
            x: 0, y: 50, z: 40
          },
          {
            id: "conic_section_theorem",
            name: "Conic Section Theorem",
            type: "formulas",
            description: "Locus equidistant from line and point forms a conic section",
            value: "Equidistant from line and point → conic section (usually parabola)",
            importance: 10,
            x: 0, y: 100, z: 40
          },
          {
            id: "step1_identify",
            name: "Step 1: Identify",
            type: "steps",
            description: "Recognize line and point",
            value: "Step 1: Line 3x + 4y = 12, Point (0,0), equidistant condition",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "step2_write_equation",
            name: "Step 2: Write Equation",
            type: "steps",
            description: "Set up equidistant equation",
            value: "Step 2: |3x + 4y - 12|/5 = √(x² + y²)",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "step3_square_both",
            name: "Step 3: Square Both",
            type: "steps",
            description: "Square both sides to eliminate square root",
            value: "Step 3: (3x + 4y - 12)² = 25(x² + y²)",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "step4_expand",
            name: "Step 4: Expand",
            type: "steps",
            description: "Expand both sides",
            value: "Step 4: 9x² + 16y² + 24xy - 72x - 96y + 144 = 25x² + 25y²",
            importance: 10,
            x: 0, y: 150, z: 20
          },
          {
            id: "step5_simplify",
            name: "Step 5: Simplify",
            type: "steps",
            description: "Collect like terms and simplify",
            value: "Step 5: 16x² + 9y² - 24xy + 72x + 96y - 144 = 0",
            importance: 10,
            x: 0, y: 200, z: 20
          },
          {
            id: "step6_classify",
            name: "Step 6: Classify",
            type: "steps",
            description: "Classify the conic section",
            value: "Step 6: This is a rotated conic section (not standard form)",
            importance: 9,
            x: 0, y: 250, z: 20
          },
          {
            id: "simplification",
            name: "Simplification",
            type: "values",
            description: "Square both sides and expand",
            value: "(3x + 4y - 12)² = 25(x² + y²)",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The locus equation",
            value: "16x² + 9y² - 24xy + 72x + 96y - 144 = 0",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "line", to: "equidistant_condition", type: "used in", strength: 9 },
          { from: "point", to: "equidistant_condition", type: "used in", strength: 9 },
          { from: "distance_from_line_formula", to: "equidistant_condition", type: "gives", strength: 10 },
          { from: "distance_formula", to: "equidistant_condition", type: "gives", strength: 10 },
          { from: "equidistant_condition", to: "simplification", type: "gives", strength: 10 },
          { from: "parabola_general_form", to: "result", type: "gives", strength: 9 },
          { from: "conic_section_theorem", to: "result", type: "gives", strength: 10 },
          { from: "simplification", to: "result", type: "equals", strength: 10 },
          { from: "step1_identify", to: "step2_write_equation", type: "enables", strength: 8 },
          { from: "step2_write_equation", to: "step3_square_both", type: "enables", strength: 9 },
          { from: "step3_square_both", to: "step4_expand", type: "enables", strength: 10 },
          { from: "step4_expand", to: "step5_simplify", type: "enables", strength: 10 },
          { from: "step5_simplify", to: "step6_classify", type: "enables", strength: 10 },
          { from: "step6_classify", to: "result", type: "equals", strength: 9 }
        ]
      }
    },
    {
      id: 'locus_hard_02',
      problem: "Find the locus of points whose distance from the circle x² + y² = 25 is equal to the distance from the point (5, 0)",
      problemType: "locus",
      difficulty: "harder",
      description: "Complex locus with circle and point",
      graphData: {
        nodes: [
          {
            id: "circle",
            name: "Circle",
            type: "concepts",
            description: "Given circle",
            value: "x² + y² = 25",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "point",
            name: "Point",
            type: "concepts",
            description: "Fixed point",
            value: "(5, 0)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "distance_from_circle_formula",
            name: "Distance from Circle Formula",
            type: "formulas",
            description: "Distance from circle center: |√(x² + y²) - r|",
            value: "Distance from circle = |√(x² + y²) - 5|",
            importance: 10,
            x: 0, y: 0, z: 40
          },
          {
            id: "distance_formula",
            name: "Distance Formula",
            type: "formulas",
            description: "Distance from circle: |√(x² + y²) - r|, Distance from point: √[(x-x₁)² + (y-y₁)²]",
            value: "Distance from circle = |√(x² + y²) - 5|, Distance from point = √[(x-5)² + y²]",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "equidistant_condition",
            name: "Equidistant Condition",
            type: "concepts",
            description: "Distance from circle = Distance from point",
            value: "|√(x² + y²) - 5| = √[(x-5)² + y²]",
            importance: 10,
            x: 150, y: -100, z: 20
          },
          {
            id: "circle_point_theorem",
            name: "Circle-Point Theorem",
            type: "formulas",
            description: "Points equidistant from circle and point lie on perpendicular bisector",
            value: "Equidistant from circle and point → perpendicular bisector",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "perpendicular_bisector_formula",
            name: "Perpendicular Bisector Formula",
            type: "formulas",
            description: "Perpendicular bisector of segment from circle center to point",
            value: "Midpoint = (2.5,0), perpendicular bisector: x = 2.5",
            importance: 9,
            x: 0, y: 100, z: 40
          },
          {
            id: "step1_identify",
            name: "Step 1: Identify",
            type: "steps",
            description: "Recognize circle and point",
            value: "Step 1: Circle x² + y² = 25 (radius 5), Point (5,0)",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "step2_write_equation",
            name: "Step 2: Write Equation",
            type: "steps",
            description: "Set up equidistant equation",
            value: "Step 2: |√(x² + y²) - 5| = √[(x-5)² + y²]",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "step3_analyze",
            name: "Step 3: Analyze",
            type: "steps",
            description: "Analyze the geometric meaning",
            value: "Step 3: Points equidistant from circle and point form a line",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "step4_apply_theorem",
            name: "Step 4: Apply Theorem",
            type: "steps",
            description: "Use circle-point theorem",
            value: "Step 4: Perpendicular bisector of segment from (0,0) to (5,0)",
            importance: 10,
            x: 0, y: 150, z: 20
          },
          {
            id: "step5_solve",
            name: "Step 5: Solve",
            type: "steps",
            description: "Solve the equation",
            value: "Step 5: x = 2.5 (vertical line)",
            importance: 10,
            x: 0, y: 200, z: 20
          },
          {
            id: "step6_verify",
            name: "Step 6: Verify",
            type: "steps",
            description: "Verify the solution",
            value: "Step 6: Check that x = 2.5 satisfies the original equation",
            importance: 9,
            x: 0, y: 250, z: 20
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The locus equation",
            value: "x = 2.5",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "circle", to: "equidistant_condition", type: "used in", strength: 9 },
          { from: "point", to: "equidistant_condition", type: "used in", strength: 9 },
          { from: "distance_from_circle_formula", to: "equidistant_condition", type: "gives", strength: 10 },
          { from: "distance_formula", to: "equidistant_condition", type: "gives", strength: 10 },
          { from: "equidistant_condition", to: "result", type: "gives", strength: 10 },
          { from: "circle_point_theorem", to: "result", type: "gives", strength: 10 },
          { from: "perpendicular_bisector_formula", to: "result", type: "gives", strength: 9 },
          { from: "step1_identify", to: "step2_write_equation", type: "enables", strength: 8 },
          { from: "step2_write_equation", to: "step3_analyze", type: "enables", strength: 9 },
          { from: "step3_analyze", to: "step4_apply_theorem", type: "enables", strength: 10 },
          { from: "step4_apply_theorem", to: "step5_solve", type: "enables", strength: 10 },
          { from: "step5_solve", to: "step6_verify", type: "enables", strength: 10 },
          { from: "step6_verify", to: "result", type: "equals", strength: 9 }
        ]
      }
    },
    {
      id: 'locus_hard_03',
      problem: "Find the locus of points whose sum of distances from three points (0, 0), (3, 0), and (0, 4) is constant",
      problemType: "locus",
      difficulty: "harder",
      description: "Complex locus with three points",
      graphData: {
        nodes: [
          {
            id: "point1",
            name: "Point 1",
            type: "concepts",
            description: "First point",
            value: "(0, 0)",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "point2",
            name: "Point 2",
            type: "concepts",
            description: "Second point",
            value: "(3, 0)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "point3",
            name: "Point 3",
            type: "concepts",
            description: "Third point",
            value: "(0, 4)",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "distance_formula",
            name: "Distance Formula",
            type: "formulas",
            description: "Distance between points: √[(x-x₁)² + (y-y₁)²]",
            value: "Distance from (0,0): √(x² + y²), Distance from (3,0): √[(x-3)² + y²], Distance from (0,4): √[x² + (y-4)²]",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "sum_condition",
            name: "Sum Condition",
            type: "concepts",
            description: "Sum of distances = constant",
            value: "√(x² + y²) + √[(x-3)² + y²] + √[x² + (y-4)²] = k",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "three_point_locus_theorem",
            name: "Three Point Locus Theorem",
            type: "formulas",
            description: "Locus with sum of distances from three points is not a standard conic",
            value: "Sum of distances from three points → complex curve",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "complex_curve_property",
            name: "Complex Curve Property",
            type: "formulas",
            description: "Curve with sum of distances from three points has no standard form",
            value: "This is not a standard conic section (ellipse, hyperbola, etc.)",
            importance: 9,
            x: 0, y: 100, z: 40
          },
          {
            id: "step1_identify",
            name: "Step 1: Identify",
            type: "steps",
            description: "Recognize the three points",
            value: "Step 1: Point 1 (0,0), Point 2 (3,0), Point 3 (0,4), sum = k",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "step2_write_equation",
            name: "Step 2: Write Equation",
            type: "steps",
            description: "Set up sum equation",
            value: "Step 2: √(x² + y²) + √[(x-3)² + y²] + √[x² + (y-4)²] = k",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "step3_analyze",
            name: "Step 3: Analyze",
            type: "steps",
            description: "Analyze the geometric meaning",
            value: "Step 3: This is not a standard conic section",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "step4_compare_with_ellipse",
            name: "Step 4: Compare with Ellipse",
            type: "steps",
            description: "Compare with standard ellipse",
            value: "Step 4: Ellipse has sum from two foci, this has three points",
            importance: 10,
            x: 0, y: 150, z: 20
          },
          {
            id: "step5_conclude",
            name: "Step 5: Conclude",
            type: "steps",
            description: "Conclude about the locus",
            value: "Step 5: Complex curve (not a standard conic)",
            importance: 10,
            x: 0, y: 200, z: 20
          },
          {
            id: "step6_verify",
            name: "Step 6: Verify",
            type: "steps",
            description: "Verify the conclusion",
            value: "Step 6: Cannot be written in standard conic form",
            importance: 9,
            x: 0, y: 250, z: 20
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The locus equation",
            value: "Complex curve (not a standard conic)",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "point1", to: "sum_condition", type: "used in", strength: 9 },
          { from: "point2", to: "sum_condition", type: "used in", strength: 9 },
          { from: "point3", to: "sum_condition", type: "used in", strength: 9 },
          { from: "distance_formula", to: "sum_condition", type: "gives", strength: 10 },
          { from: "sum_condition", to: "result", type: "gives", strength: 10 },
          { from: "three_point_locus_theorem", to: "result", type: "gives", strength: 10 },
          { from: "complex_curve_property", to: "result", type: "gives", strength: 9 },
          { from: "step1_identify", to: "step2_write_equation", type: "enables", strength: 8 },
          { from: "step2_write_equation", to: "step3_analyze", type: "enables", strength: 9 },
          { from: "step3_analyze", to: "step4_compare_with_ellipse", type: "enables", strength: 10 },
          { from: "step4_compare_with_ellipse", to: "step5_conclude", type: "enables", strength: 10 },
          { from: "step5_conclude", to: "step6_verify", type: "enables", strength: 10 },
          { from: "step6_verify", to: "result", type: "equals", strength: 9 }
        ]
      }
    },
    {
      id: 'locus_hard_04',
      problem: "Find the locus of points whose product of distances from two points (2, 0) and (-2, 0) is 4",
      problemType: "locus",
      difficulty: "harder",
      description: "Product of distances locus",
      graphData: {
        nodes: [
          {
            id: "point1",
            name: "Point 1",
            type: "concepts",
            description: "First point",
            value: "(2, 0)",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "point2",
            name: "Point 2",
            type: "concepts",
            description: "Second point",
            value: "(-2, 0)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "distance_formula",
            name: "Distance Formula",
            type: "formulas",
            description: "Distance between points: √[(x-x₁)² + (y-y₁)²]",
            value: "Distance from (2,0): √[(x-2)² + y²], Distance from (-2,0): √[(x+2)² + y²]",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "product_condition",
            name: "Product Condition",
            type: "concepts",
            description: "Product of distances = 4",
            value: "√[(x-2)² + y²] × √[(x+2)² + y²] = 4",
            importance: 10,
            x: 150, y: -100, z: 20
          },
          {
            id: "product_of_distances_theorem",
            name: "Product of Distances Theorem",
            type: "formulas",
            description: "Locus with product of distances from two points forms a special curve",
            value: "Product of distances from two points → special curve",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "lemniscate_property",
            name: "Lemniscate Property",
            type: "formulas",
            description: "Product of distances from foci can form a lemniscate",
            value: "Product of distances from foci = constant → lemniscate-like curve",
            importance: 9,
            x: 0, y: 100, z: 40
          },
          {
            id: "step1_identify",
            name: "Step 1: Identify",
            type: "steps",
            description: "Recognize the two points and product condition",
            value: "Step 1: Point 1 (2,0), Point 2 (-2,0), product = 4",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "step2_write_equation",
            name: "Step 2: Write Equation",
            type: "steps",
            description: "Set up product equation",
            value: "Step 2: √[(x-2)² + y²] × √[(x+2)² + y²] = 4",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "step3_square_both",
            name: "Step 3: Square Both",
            type: "steps",
            description: "Square both sides to eliminate square roots",
            value: "Step 3: [(x-2)² + y²][(x+2)² + y²] = 16",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "step4_expand",
            name: "Step 4: Expand",
            type: "steps",
            description: "Expand the product",
            value: "Step 4: (x² - 4x + 4 + y²)(x² + 4x + 4 + y²) = 16",
            importance: 10,
            x: 0, y: 150, z: 20
          },
          {
            id: "step5_simplify",
            name: "Step 5: Simplify",
            type: "steps",
            description: "Simplify and collect terms",
            value: "Step 5: (x² + y²)² - 4x² + 4y² = 0",
            importance: 10,
            x: 0, y: 200, z: 20
          },
          {
            id: "step6_analyze",
            name: "Step 6: Analyze",
            type: "steps",
            description: "Analyze the resulting curve",
            value: "Step 6: This is a special curve (not a standard conic)",
            importance: 9,
            x: 0, y: 250, z: 20
          },
          {
            id: "simplification",
            name: "Simplification",
            type: "values",
            description: "Square both sides and expand",
            value: "[(x-2)² + y²][(x+2)² + y²] = 16",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The locus equation",
            value: "(x² + y²)² - 4x² + 4y² = 0",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "point1", to: "product_condition", type: "used in", strength: 9 },
          { from: "point2", to: "product_condition", type: "used in", strength: 9 },
          { from: "distance_formula", to: "product_condition", type: "gives", strength: 10 },
          { from: "product_condition", to: "simplification", type: "gives", strength: 10 },
          { from: "product_of_distances_theorem", to: "result", type: "gives", strength: 10 },
          { from: "lemniscate_property", to: "result", type: "gives", strength: 9 },
          { from: "simplification", to: "result", type: "equals", strength: 10 },
          { from: "step1_identify", to: "step2_write_equation", type: "enables", strength: 8 },
          { from: "step2_write_equation", to: "step3_square_both", type: "enables", strength: 9 },
          { from: "step3_square_both", to: "step4_expand", type: "enables", strength: 10 },
          { from: "step4_expand", to: "step5_simplify", type: "enables", strength: 10 },
          { from: "step5_simplify", to: "step6_analyze", type: "enables", strength: 10 },
          { from: "step6_analyze", to: "result", type: "equals", strength: 9 }
        ]
      }
    },
    {
      id: 'locus_hard_05',
      problem: "Find the locus of points whose ratio of distances from (0, 0) and (4, 0) is 2:1",
      problemType: "locus",
      difficulty: "harder",
      description: "Ratio of distances locus",
      graphData: {
        nodes: [
          {
            id: "point1",
            name: "Point 1",
            type: "concepts",
            description: "First point",
            value: "(0, 0)",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "point2",
            name: "Point 2",
            type: "concepts",
            description: "Second point",
            value: "(4, 0)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "distance_formula",
            name: "Distance Formula",
            type: "formulas",
            description: "Distance between points: √[(x-x₁)² + (y-y₁)²]",
            value: "Distance from (0,0): √(x² + y²), Distance from (4,0): √[(x-4)² + y²]",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "ratio_condition",
            name: "Ratio Condition",
            type: "concepts",
            description: "Ratio of distances = 2:1",
            value: "√(x² + y²) = 2√[(x-4)² + y²]",
            importance: 10,
            x: 150, y: -100, z: 20
          },
          {
            id: "apollonius_circle_theorem",
            name: "Apollonius Circle Theorem",
            type: "formulas",
            description: "Ratio of distances from two points forms a circle",
            value: "PA = k·PB → circle (Apollonius circle)",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "apollonius_circle_formula",
            name: "Apollonius Circle Formula",
            type: "formulas",
            description: "Circle with ratio k: (x² + y²) = k²[(x-a)² + y²]",
            value: "For ratio 2:1, k = 2",
            importance: 9,
            x: 0, y: 100, z: 40
          },
          {
            id: "circle_parameters",
            name: "Circle Parameters",
            type: "formulas",
            description: "For ratio k, center at (k²a/(k²-1), 0), radius = ka/(k²-1)",
            value: "k = 2, a = 4 → center (16/3, 0), radius = 8/3",
            importance: 10,
            x: 0, y: 150, z: 40
          },
          {
            id: "step1_identify",
            name: "Step 1: Identify",
            type: "steps",
            description: "Recognize the two points and ratio condition",
            value: "Step 1: Point 1 (0,0), Point 2 (4,0), ratio = 2:1",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "step2_write_equation",
            name: "Step 2: Write Equation",
            type: "steps",
            description: "Set up ratio equation",
            value: "Step 2: √(x² + y²) = 2√[(x-4)² + y²]",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "step3_square_both",
            name: "Step 3: Square Both",
            type: "steps",
            description: "Square both sides to eliminate square roots",
            value: "Step 3: x² + y² = 4[(x-4)² + y²]",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "step4_expand",
            name: "Step 4: Expand",
            type: "steps",
            description: "Expand the right side",
            value: "Step 4: x² + y² = 4(x² - 8x + 16 + y²)",
            importance: 10,
            x: 0, y: 150, z: 20
          },
          {
            id: "step5_simplify",
            name: "Step 5: Simplify",
            type: "steps",
            description: "Collect like terms and simplify",
            value: "Step 5: 3x² + 3y² - 32x + 64 = 0",
            importance: 10,
            x: 0, y: 200, z: 20
          },
          {
            id: "step6_complete_square",
            name: "Step 6: Complete Square",
            type: "steps",
            description: "Complete the square to find center and radius",
            value: "Step 6: (x-16/3)² + y² = (8/3)²",
            importance: 10,
            x: 0, y: 250, z: 20
          },
          {
            id: "simplification",
            name: "Simplification",
            type: "values",
            description: "Square both sides and expand",
            value: "x² + y² = 4[(x-4)² + y²]",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The locus equation",
            value: "(x-16/3)² + y² = 64/9",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "point1", to: "ratio_condition", type: "used in", strength: 9 },
          { from: "point2", to: "ratio_condition", type: "used in", strength: 9 },
          { from: "distance_formula", to: "ratio_condition", type: "gives", strength: 10 },
          { from: "ratio_condition", to: "simplification", type: "gives", strength: 10 },
          { from: "apollonius_circle_theorem", to: "result", type: "gives", strength: 10 },
          { from: "apollonius_circle_formula", to: "result", type: "gives", strength: 9 },
          { from: "circle_parameters", to: "result", type: "gives", strength: 10 },
          { from: "simplification", to: "result", type: "equals", strength: 10 },
          { from: "step1_identify", to: "step2_write_equation", type: "enables", strength: 8 },
          { from: "step2_write_equation", to: "step3_square_both", type: "enables", strength: 9 },
          { from: "step3_square_both", to: "step4_expand", type: "enables", strength: 10 },
          { from: "step4_expand", to: "step5_simplify", type: "enables", strength: 10 },
          { from: "step5_simplify", to: "step6_complete_square", type: "enables", strength: 10 },
          { from: "step6_complete_square", to: "result", type: "equals", strength: 10 }
        ]
      }
    }
  ]
}; 